<template>
  <div>
    <b-breadcrumb :items="breadcrumbItems"></b-breadcrumb>

    <div class="row mb-3">
      <div class="col-sm-4">
        <h4 qid="retention-rules-page-title" class="mb-2">
          {{ $t('maintenance.retention_rules.title') }}
        </h4>
        <div>
          <span v-if="getGuide('retention_rules_list')">
            <GuideButton :guide="getGuide('retention_rules_list')"></GuideButton>
          </span>
        </div>
      </div>
      <div class="col-sm-8 text-right">
        <button
            class="btn btn-secondary-dark float-right"
            @click="filterOpened = !filterOpened"
            qid="retention-rules-create-button"
        >
          <font-awesome-icon icon="filter"/>
        </button>
        <router-link
            to="/maintenance/retention-rules/create"
            class="btn btn-success float-right"
            qid="create-rr-button"
            v-if="isGranted(permissions.RETENTION_RULE_CREATE)"
        >
          <font-awesome-icon icon="plus-circle"/> {{ $t('maintenance.retention_rules.create_button') }}
        </router-link>
        <b-form-input
          type="text"
          v-model="filters.keywordValue"
          class="float-right mw-300 mr-3 search-bar"
          qid="search-rr-input"
          v-on:input="debounceInput"
          :placeholder="$t('maintenance.retention_rules.search_placeholder')"
        ></b-form-input>
      </div>
    </div>

    <div class="row" :class="{'table-with-filters': filterOpened}">
      <div class="col-sm-10 table-container">
        <div class="card">
          <table class="table table-responsive-md">
            <thead>
            <tr>
              <th scope="col"
                  class="cursor-pointer"
                  @click="selectSort('name')"
                  qid="sort-rr-name"
              >
                {{ $t('maintenance.retention_rules.table.name') }}
                <span class="ml-2">
                  <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'name'"/>
                  <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'name'"/>
                  <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'name'"/>
                </span>
              </th>
              <th scope="col"
                  class="cursor-pointer"
                  @click="selectSort('time_limit')"
                  qid="sort-rr-time-limit"
              >
                {{ $t('maintenance.retention_rules.table.time_limit') }}
                <span class="ml-2">
                  <font-awesome-icon class="text-muted" icon="sort" v-if="selectedSort !== 'time_limit'"/>
                  <font-awesome-icon icon="caret-down" v-if="selectedDirection === 'desc' && selectedSort === 'time_limit'"/>
                  <font-awesome-icon icon="caret-up" v-if="selectedDirection === 'asc' && selectedSort === 'time_limit'"/>
                </span>
              </th>
              <th width="130"></th>
            </tr>
            </thead>
            <tbody>
              <tr v-for="object in tableItems">
                <td>
                  <router-link
                      :to="{ name: 'maintenance.retention_rules.view', params: { slug: object.slug }}"
                      qid="rr-view-page-link"
                  >
                    {{ object.name }}
                  </router-link>
                </td>
                <td>
                  <span qid="rr-time-limit">
                    {{ object.time_limit }}
                  </span>
                </td>
                <td>
                  <span v-if="isGranted([permissions.RETENTION_RULE_EDIT, permissions.RETENTION_RULE_EDIT_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-edit"
                        qid="edit-rr-modal"
                        @click="showEditRRModal(object)"
                  >
                    <font-awesome-icon icon="pen"/>
                  </span>
                  <span v-if="isGranted([permissions.RETENTION_RULE_DELETE, permissions.RETENTION_RULE_DELETE_OWN], object.created_by) && (!getClient.parent || (getClient.parent && object.partition))"
                        class="btn btn-delete"
                        qid="delete-rr-modal"
                        @click="showConfirmDeleteModal(object)"
                  >
                    <font-awesome-icon icon="trash-alt"/>
                  </span>
                </td>
              </tr>
              <tr v-if="!loading && !tableItems.length" qid="no-results-row" class="text-center">
                <td colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mt-3 pagination row">
          <div class="col-md-6">
            <div class="row">
              <div class="float-left">
                <span class="text-muted pagination-counter">
                  {{ $t('pagination.entries_per_page') }}:
                </span>
              </div>
              <div class="float-left">
                <multiselect
                    v-model="perPage"
                    :options="perPageOptions"
                    track-by="value"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    label="value"
                    qid="create-org-form-organization-level"
                    @select="perPageChanged"
                    :placeholder="selectedPerPage"
                    @remove="perPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <b-pagination
                class="float-right"
                v-model="currentPage"
                :total-rows="rows"
                :per-page="selectedPerPage"
                aria-controls="my-table"
                @change="pageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                <span v-if="active">{{getPerPage}}</span>
              </span>
            </b-pagination>
          </div>
        </div>
      </div>
      <div class="col-sm-2 filter-container">
        <div class="card">
          <div class="card-body">
            <b-form-group>
              <label qid="edit-rr-type-label">
                {{ $t('maintenance.retention_rules.create.time_limit_label') }}
              </label>
              <multiselect
                  v-model="filters.time_limit"
                  :options="timeLimitItems"
                  :placeholder="$t('maintenance.retention_rules.create.time_limit_placeholder')"
                  track-by="time_limit"
                  @select="debounceInput"
                  @remove="debounceInput"
                  select-label="✔"
                  selected-label="✔"
                  deselect-label="x"
                  label="time_limit"
                  qid="filter-rr-time-limit"
              >
              </multiselect>
            </b-form-group>
            <a @click="resetFilters" class="text-right" qid="filter-rr-reset">
              {{ $t('system.reset_filters') }}
            </a>
          </div>
        </div>
      </div>
    </div>


    <!-- Delete retention rule confirm modal template -->
    <b-modal ref="delete-retention-rule" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-rr-title">
          {{ $t('maintenance.retention_rules.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('maintenance.retention_rules.delete.description') }} {{ itemToDelete.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('maintenance.retention_rules.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmDeleteModal"
                  qid="delete-rr-cancel-button"
        >
          {{ $t('maintenance.retention_rules.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteRR(itemToDelete)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-rr-submit-button"
                  v-if="isGranted([permissions.RETENTION_RULE_DELETE, permissions.RETENTION_RULE_DELETE_OWN], itemToDelete.created_by) && (!getClient.parent || (getClient.parent && itemToDelete.partition))"
        >
          {{ $t('maintenance.retention_rules.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit retention rule modal template -->
    <b-modal ref="edit-retention-rule" hide-footer>
      <div class="d-block text-center">
        <h4 qid="update-rr-title">
          {{ $t('maintenance.retention_rules.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="edit-rr-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="editRRFormSubmit" qid="edit-rr-form">
        <div class="form-container">
          <b-form-group>
            <label qid="edit-rr-name-label">
              {{ $t('maintenance.retention_rules.create.name_label') }}
            </label>
            <b-form-input
              qid="edit-rr-form-name"
              type="text"
              v-model="rrForm.name"
              :maxLength="255"
              required
              :placeholder="$t('maintenance.retention_rules.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="edit-rr-description-label">
              {{ $t('maintenance.retention_rules.create.description_label') }}
            </label>
            <b-form-textarea
              qid="edit-rr-form-description"
              v-model="rrForm.description"
              rows="6"
              max-rows="6"
              :placeholder="$t('maintenance.retention_rules.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>
          <b-form-group :class="{'multiselect--show-input': timeLimitInput.length}">
            <label qid="edit-rr-type-label">
              {{ $t('maintenance.retention_rules.create.time_limit_label') }}
            </label>
            <multiselect
                v-model="rrForm.time_limit"
                :options="timeLimitItems"
                :placeholder="$t('maintenance.retention_rules.create.time_limit_placeholder')"
                :preserveSearch="true"
                @search-change="handleTimeLimitInput"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                track-by="time_limit"
                label="time_limit"
                qid="edit-rr-form-time-limit"
            >
              <template slot="noResult" slot-scope="props">
                {{ $t('maintenance.retention_rules.create.time_limit_no_results') }}
              </template>
            </multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideEditRRModal"
                    qid="edit-rr-cancel-button"
          >
            {{ $t('maintenance.retention_rules.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="edit-rr-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('maintenance.retention_rules.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'RetentionRules',
    data: function() {
      return {
        breadcrumbItems: [
          {
            text: this.$t('dashboard.title'),
            to: '/'
          },
          {
            text: this.$t('maintenance.title'),
            to: '/maintenance'
          },
          {
            text: this.$t('maintenance.retention_rules.title'),
            active: true
          }
        ],
        tableItems: [],

        // Pagination
        perPageOptions: [
          { name: '10', value: '10' },
          { name: '20', value: '20' },
          { name: '50', value: '50' },
          { name: '100', value: '100' }
        ],
        perPage: '',
        selectedPerPage: "10",
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        rrForm: {
          name: '',
          description: '',
          time_limit: [],
          client:'',
        },
        error: '',
        selectedSort: '',
        selectedDirection: '',
        filterOpened: false,
        keywordValue: '',
        filters: {
          time_limit: '',
          keywordValue: ''
        },
        timeLimitItems: [],
        timeLimitInput: '',
        loading: true,
        buttonDisabled: false,
        myGuides: [],
      }
    },
    computed: {
      rows() {
        // Use computed value for rows because of dynamic update
        return this.totalRows
      },
      getClient() {
        return this.$store.state.client
      },
      getFilters() {
        return this.$store.state.activeFilters
      },
      getStorePerPage() {
        return this.$store.state.perPage
      },
      getPerPage() {
        let page = this.currentPage;
        let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
        let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
        let selectedOf = this.totalRows;

        if(selectedTo > selectedOf){
          selectedTo = selectedOf
        }

        return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadContent();
        this.loadLimits();
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent()
      }, 400),
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Maintenance' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Maintenance',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      resetFilters() {
        this.filters.time_limit = '',
        this.debounceInput()
      },
      handleTimeLimitInput(input) {
        this.timeLimitInput = input;
      },
      selectSort(sort) {
        // If old and new sort are same, switch just direction
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          // If different sort, set sort and set direction to default
          this.selectedDirection = 'desc'
          this.selectedSort = sort;
        }
        this.loadContent();
      },
      loadContent(page, perPage) {

        if (this.getStorePerPage) {
          this.selectedPerPage = this.getStorePerPage;
        }

        // If page is not defined, set page to default 1
        let pageNumber = page ? page : 1;
        let perPageNumber = perPage ? perPage : this.selectedPerPage;
        let self = this;

        if (
          this.filters.keywordValue !== '' ||
          (this.filters.time_limit && this.filters.time_limit.time_limit)
        ) {
          this.filtersOpened = true
        }

        piincHttp.get('retention-rules', { params:
          {
            page: pageNumber,
            per_page: perPageNumber,
            sort: this.selectedSort !== '' ? this.selectedSort : undefined,
            order: this.selectedDirection !== '' ? this.selectedDirection : undefined,
            keyword: this.filters.keywordValue !== '' ? this.filters.keywordValue : undefined,
            time_limit: this.filters.time_limit ? this.filters.time_limit.time_limit : undefined,
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
          // Set number of total items
          self.totalRows = response.data.total_count;
        }, function() {});
      },
      pageChanged(page) {
        this.loadContent(page)
      },
      perPageChanged(perPage) {
        let self = this;
        // Load content when page changed listener is triggered
        this.loadContent(1, perPage.value)
        self.selectedPerPage = perPage.value
        this.$store.commit('storePerPage', this.selectedPerPage);
      },
      perPageRemoved() {
        let self = this;
        // Load content when page changed listener is triggered
        self.selectedPerPage = self.perPageOptions[0].value
        this.loadContent(1, self.selectedPerPage)
      },
      showConfirmDeleteModal(object) {
        this.itemToDelete = object
        this.$refs['delete-retention-rule'].show()
      },
      hideConfirmDeleteModal() {
        this.itemToDelete = {}
        this.$refs['delete-retention-rule'].hide()
      },
      showEditRRModal(object) {
        let self = this;

        // Preselect category on edit modal
        this.rrForm.time_limit = []
        if (object.time_limit) {
          _.forEach(this.timeLimitItems, function(item) {
            if (item.time_limit == object.time_limit) {
              self.rrForm.time_limit = item;
            }
          });
        }

        this.rrForm.slug = object.slug;
        this.rrForm.name = object.name;
        this.rrForm.description = object.description;
        this.$refs['edit-retention-rule'].show()
      },
      hideEditRRModal() {
        this.timeLimitInput = ''
        this.$refs['edit-retention-rule'].hide()
      },
      editRRFormSubmit(evt) {
        this.buttonDisabled = true;
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        this.rrForm.client = this.getClient.slug;

        if (this.timeLimitInput && this.timeLimitInput.length) {
          this.rrForm.time_limit = this.timeLimitInput
        } else {
          if (this.rrForm.time_limit && this.rrForm.time_limit.time_limit) {
            this.rrForm.time_limit = this.rrForm.time_limit.time_limit
          } else {
            this.rrForm.time_limit = '';
          }
        }

        piincHttp.put('retention-rules/' + this.rrForm.slug, this.rrForm).then(function(response) {
          self.hideEditRRModal();
          self.loadLimits();
          self.buttonDisabled = false;
          self.loadContent(self.currentPage);
          self.$toastr('success', self.$t('maintenance.retention_rules.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      },
      confirmDeleteRR(object) {
        let self = this;
        piincHttp.delete('retention-rules/' + object.slug).then(function(response) {
          self.hideConfirmDeleteModal();
          self.loadContent(self.currentPage);
          // Trigger toastr message after modal close
          self.$toastr('success', self.$t('maintenance.retention_rules.delete.success'))
        }, function() {});
      },
      loadLimits() {
        let self = this;
        piincHttp.get('retention-rules/limits', { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.timeLimitItems = response.data
          if (self.timeLimitItems && !self.timeLimitItems.length) {
            self.timeLimitItems.push({time_limit: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      }
    },
    components: {
      GuideButton
    },
    beforeRouteLeave(to, from, next) {
      let object = {
        type: 'retention_rules',
        filters: this.filters
      }
      this.$store.commit('storeActiveFilters', object);
      this.$store.commit('storePerPage', this.selectedPerPage);
      next()
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        if (this.getFilters.retention_rules) {
          this.filters = this.getFilters.retention_rules
        }
        this.loadContent();
        this.loadLimits();
      }
      this.loadGuides('retention_rules_list');
    }
  }
</script>
